import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import { useContext, useEffect, useState } from 'react';

import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ApplicationCycleSelectOption } from '@/models/ApplicationCycle';
import { ProgramNames } from '@/models/Program';
import useApplicationCycle from '@/hooks/useApplicationCycle';

function SelectApplicationCycle({
  includeNoValueSelection = false,
  noValueSelectionLabel = 'All Years',
  onlyShowAppCyclesWithApplications = false,
  value,
  label,
  onChange,
  styleProps,
  disabled = false,
}: {
  includeNoValueSelection?: boolean;
  noValueSelectionLabel?: string;
  onlyShowAppCyclesWithApplications?: boolean;
  value?: any;
  label?: string;
  onChange?: (e: SelectChangeEvent) => void;
  styleProps?: any;
  disabled?: boolean;
}) {
  const theme = useTheme();
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { getApplicationCyclesSelectOptions } = useApplicationCycle();

  const [selectedApplicationCycleYear, setSelectedApplicationCycleYear] = useState<string | null>(
    null
  );
  const [applicationCycleSelectOptions, setApplicationCycleSelectOptions] = useState<
    number[] | null
  >(null);
  const selectedApplicationCycleLabel = label ? label : 'Select Submission Year';
  const styles = styleProps ?? {
    textAlign: 'center',
    minWidth: '500px',
    alignSelf: 'center',
  };

  useEffect(() => {
    setSelectedApplicationCycleYear(value);
  }, [value]);

  useEffect(() => {
    const updateSelectOptions = (appCycles: ApplicationCycleSelectOption[]) => {
      const selectOptions = [
        ...new Set(
          appCycles.map((applicationCycle: ApplicationCycleSelectOption) =>
            applicationCycle.programId == ProgramNames.TreeLineUSA
              ? applicationCycle.year - 1
              : applicationCycle.year
          )
        ),
      ].sort((a: number, b: number) => b - a);

      setApplicationCycleSelectOptions(selectOptions);
    };

    if (!onlyShowAppCyclesWithApplications) {
      const appCyclesForSelectOptions = applicationCycles.map(
        (ac) => ac satisfies ApplicationCycleSelectOption
      ) satisfies ApplicationCycleSelectOption[];
      updateSelectOptions(appCyclesForSelectOptions);
    } else {
      getApplicationCyclesSelectOptions().then((response) => {
        updateSelectOptions(response);
      });
    }
  }, [applicationCycles, onlyShowAppCyclesWithApplications, getApplicationCyclesSelectOptions]);

  function handleClearClick() {
    setSelectedApplicationCycleYear('');
    onChange({ target: { value: '' } } as SelectChangeEvent);
  }

  return (
    <Box sx={{ styles }}>
      <FormControl fullWidth>
        <InputLabel id='select-application-cycle-label'>{selectedApplicationCycleLabel}</InputLabel>
        <Select
          disabled={disabled}
          id={'select-application-cycle'}
          style={{ minWidth: '200px' }}
          labelId='select-application-cycle-label'
          label={selectedApplicationCycleLabel}
          value={
            selectedApplicationCycleYear && applicationCycleSelectOptions
              ? selectedApplicationCycleYear
              : ''
          }
          onChange={(e: SelectChangeEvent) => {
            setSelectedApplicationCycleYear(e.target.value);

            if (onChange) {
              onChange(e);
            }
          }}
          endAdornment={
            <IconButton
              onClick={handleClearClick}
              sx={{ display: selectedApplicationCycleYear ? '' : 'none' }}
            >
              <ClearIcon />
            </IconButton>
          }
          sx={{
            backgroundColor: theme.palette.sandTint.main,
            '& .MuiSelect-iconOutlined': {
              display: selectedApplicationCycleYear ? 'none' : '',
            },
          }}
        >
          {includeNoValueSelection && (
            <MenuItem value={noValueSelectionLabel}>
              <em>{noValueSelectionLabel}</em>
            </MenuItem>
          )}

          {applicationCycleSelectOptions?.map((year: any) => {
            return (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
}

export default SelectApplicationCycle;
